import { startLoading, stopLoading } from "../common/utils/button-loading";
import { LikeCommentResponse } from "./interfaces/http";
import { likeComment } from "./services/comment-service";

export default function setupLikeComment() {
	const { getClosest } = pm.utils.dom;

	let isProcessing: Record<string, boolean> = {};

	document.addEventListener("click", handleDocumentClick);

	async function handleDocumentClick(e: MouseEvent) {
		const target = e.target as HTMLElement | null;
		if (!target) return;

		const likeButton = getClosest(target, ".like-comment", 10);
		if (!likeButton || !likeButton.tagName) return;

		await likeOrUnlikeComment(likeButton);
	}

	async function likeOrUnlikeComment(button: HTMLElement) {
		const commentId = button.dataset.commentId
			? parseInt(button.dataset.commentId, 10)
			: 0;

		const commentKey = "comment_" + commentId;

		if (!isProcessing.hasOwnProperty(commentKey)) {
			isProcessing[commentKey] = false;
		}

		if (isProcessing[commentKey]) return;

		const wrapper = document.querySelector(
			"#comment-" + button.dataset.commentId,
		) as HTMLElement | null;
		if (!wrapper) return;

		isProcessing[commentKey] = true;
		if (button) startLoading(button);

		const actionType = button.classList.contains("is-liked")
			? "unlike"
			: "like";

		// Let's visually like/unlike the comment even before the ajax request is started.
		if (actionType === "like") {
			visuallyLikeComment(button);
		} else {
			visuallyUnlikeComment(button);
		}

		const response: LikeCommentResponse = await likeComment(
			commentId,
			actionType,
		);

		if (response.success) {
			updateCounts(button, response.totalLikes ? response.totalLikes : 0);
		} else {
			// If action is failed, revert the button status.
			if (actionType === "like") {
				visuallyUnlikeComment(button);
			} else {
				visuallyLikeComment(button);
			}
		}

		isProcessing[commentKey] = false;
		if (button) stopLoading(button);
	}

	/**
	 * Visually like a comment.
	 *
	 * @param button The targetted button.
	 */
	function visuallyLikeComment(button: HTMLElement) {
		button.classList.add("is-liked");

		const iconTitle = button.querySelector("svg title");
		if (iconTitle) iconTitle.innerHTML = "Unlike this comment";
	}

	/**
	 * Visually unlike a comment.
	 *
	 * @param button The targetted button.
	 */
	function visuallyUnlikeComment(button: HTMLElement) {
		button.classList.remove("is-liked");

		const iconTitle = button.querySelector("svg title");
		if (iconTitle) iconTitle.innerHTML = "Like this comment";
	}

	/**
	 * Update the comment counts.
	 *
	 * @param button The targetted button.
	 * @param totalLikes The total likes.
	 */
	function updateCounts(button: HTMLElement, totalLikes: number) {
		const el = button.parentNode!.querySelector(".total-likes");
		if (!el) return;

		el.textContent = totalLikes.toString();

		if (totalLikes > 0) {
			el.classList.remove("is-hidden");
		} else {
			el.classList.add("is-hidden");
		}
	}
}
